// ============================================================
// SkyPlus Dynamic Menu Models
// app/components/skyplus/shared/menu/menu.model.ts
// ============================================================

export interface MenuItemDto {
  menuKey:      string;
  label:        string;
  icon:         string;
  route:        string | null;
  sortOrder:    number;
  color:        string | null;
  menuType:     'HEADER' | 'SIDEBAR_CATEGORY' | 'SIDEBAR_ITEM';
  workflowType: string | null;
  /** True when shown as a locked "showroom" teaser — not licensed, displayed as an upsell. */
  locked?:      boolean | null;
  /** Upsell copy for a locked item. */
  teaser?:      string | null;
  children:     MenuItemDto[] | null;
}

export interface MenuResponse {
  items:              MenuItemDto[];
  /** Per-user seat access mode: FULL = normal, READ_ONLY = license seats exhausted. */
  accessMode?:        'FULL' | 'READ_ONLY';
  licenseClient:      string;
  licenseValid:       boolean;
  fingerprintWarning: boolean;
  /** NONE | INFO | WARNING | ALERT | CRITICAL | URGENT | EXPIRED */
  expiryWarningLevel: ExpiryWarningLevel;
  /** Days until expiry. -1 means no expiry set. */
  daysUntilExpiry:    number;
  /** ISO date string e.g. 2027-01-01 */
  expiryDate:         string | null;
}

export type ExpiryWarningLevel =
  | 'NONE'
  | 'INFO'
  | 'WARNING'
  | 'ALERT'
  | 'CRITICAL'
  | 'URGENT'
  | 'EXPIRED';

export interface ExpiryBadgeConfig {
  level:       ExpiryWarningLevel;
  label:       string;
  bgColor:     string;
  textColor:   string;
  borderColor: string;
  icon:        string;
  fullBanner:  boolean;  // true = EXPIRED — shows full-width blocking banner
}

/** Maps warning levels to visual config used by the header component */
export const EXPIRY_BADGE_CONFIG: Record<ExpiryWarningLevel, ExpiryBadgeConfig> = {
  NONE:     { level: 'NONE',     label: '',                                bgColor: 'transparent', textColor: 'transparent', borderColor: 'transparent', icon: '',                  fullBanner: false },
  INFO:     { level: 'INFO',     label: 'License expires in {d} days',     bgColor: '#E3F2FD',     textColor: '#0D47A1',      borderColor: '#42A5F5',     icon: 'info',              fullBanner: false },
  WARNING:  { level: 'WARNING',  label: 'License expires in {d} days',     bgColor: '#FFF8E1',     textColor: '#E65100',      borderColor: '#FFB300',     icon: 'warning_amber',     fullBanner: false },
  ALERT:    { level: 'ALERT',    label: 'License expires in {d} days',     bgColor: '#FFF3E0',     textColor: '#BF360C',      borderColor: '#FF6D00',     icon: 'warning',           fullBanner: false },
  CRITICAL: { level: 'CRITICAL', label: 'License expires in {d} days!',    bgColor: '#FFEBEE',     textColor: '#B71C1C',      borderColor: '#F44336',     icon: 'error_outline',     fullBanner: false },
  URGENT:   { level: 'URGENT',   label: 'License expires in {d} days!!!',  bgColor: '#F44336',     textColor: '#FFFFFF',      borderColor: '#D32F2F',     icon: 'error',             fullBanner: false },
  EXPIRED:  { level: 'EXPIRED',  label: 'License has EXPIRED',             bgColor: '#B71C1C',     textColor: '#FFFFFF',      borderColor: '#7F0000',     icon: 'block',             fullBanner: true  },
};

/**
 * WorkflowCategory — maps from SIDEBAR_CATEGORY MenuItemDto.
 * Keeps the same shape as the old workflow.config.ts so the template
 * needs minimal changes.
 */
export interface WorkflowCategory {
  id:          string;   // menuKey
  name:        string;   // label
  icon:        string;
  color:       string;
  expanded:    boolean;
  workflowType:string | null;
  stages:      WorkflowStage[];
}

/**
 * WorkflowStage — maps from SIDEBAR_ITEM MenuItemDto.
 */
export interface WorkflowStage {
  id:           string;   // menuKey
  name:         string;   // label
  icon:         string;
  workflowType: string | null;
  description?: string;   // optional subtitle shown in the content header
}
